<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const ctx = useMessageContext();
const { t } = useI18n();

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-gray-3 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'bg-n-gray-3 w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]: 'rounded-xl rounded-bl-sm',
  [ORIENTATION.RIGHT]: 'rounded-xl rounded-br-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[ctx.orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[ctx.variant.value]];

  if (ctx.variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[ctx.orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: this.message.id,
  });
};

const previewMessage = computed(() => {
  if (!ctx.inReplyTo) return '';

  const { content, attachments } = ctx.inReplyTo;

  if (content) return content;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});

// Reaction rail data (from provider + camelCase fallback)
const reactionsSummary = computed(
  () => ctx.reactionsSummary?.value || ctx.reactions_summary?.value || {}
);
const myReaction = computed(
  () => ctx.myReaction?.value || ctx.my_reaction?.value || null
);
const isZaloPersonal = computed(() => ctx.isZaloPersonal?.value === true);
const hasReactions = computed(() => Object.keys(reactionsSummary.value).length > 0);
const reactionRailStyle = computed(() => {
  // Left if: self has myReaction OR (Zalo Personal and it's my message with any reactions)
  const shouldLeft =
    (myReaction.value && hasReactions.value) ||
    (isZaloPersonal.value && ctx.isMyMessage.value && hasReactions.value);
  return shouldLeft ? { left: '6px' } : { right: '6px' };
});
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        'max-w-lg': ctx.variant.value !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="ctx.inReplyTo"
      class="bg-n-alpha-black1 rounded-lg p-2"
      @click="scrollToMessage"
    >
      <span class="line-clamp-2">
        {{ previewMessage }}
      </span>
    </div>
    <slot />

    <!-- Reaction rail: tách khỏi bubble, chip lấn 50% lên -->
    <div v-if="isZaloPersonal && hasReactions" class="relative h-5 mt-1">
      <div
        class="absolute top-0 -translate-y-1/2 text-xs bg-white/90 border border-slate-200 rounded-full px-2 py-0.5 shadow-sm flex items-center gap-1"
        :style="reactionRailStyle"
      >
        <span v-for="(count, emoji) in reactionsSummary" :key="emoji">
          {{ emoji }} {{ count }}
        </span>
      </div>
    </div>

    <div
      v-if="ctx.pinned.value"
      class="flex items-center justify-end mt-1"
      :class="flexOrientationClass"
    >
      <span class="text-xs text-n-slate-11 flex items-center">
        <fluent-icon icon="pin" size="12" class="mr-1" />
        {{ t('CONVERSATION.PINNED_MESSAGES.PINNED_INDICATOR') }}
      </span>
    </div>
    <MessageMeta
      v-if="!ctx.shouldGroupWithNext.value && ctx.variant.value !== MESSAGE_VARIANTS.ACTIVITY"
      :class="[
        flexOrientationClass,
        ctx.variant.value === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        ctx.variant.value === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : 'text-n-slate-11',
      ]"
      class="mt-2"
    />
  </div>
</template>
